import apiClient from './api/client';
import ENDPOINTS from './api/endpoints';

/**
 * Service for table reservation management
 */
export const reservationService = {
  /**
   * Create a new reservation
   */
  create: async (reservationData) => {
    try {
      return await apiClient.post(ENDPOINTS.TABLE_RESERVATIONS.CREATE, reservationData);
    } catch (error) {
      console.error('Failed to create reservation:', error);
      throw error;
    }
  },

  /**
   * Update an existing reservation
   */
  update: async (id, reservationData) => {
    try {
      return await apiClient.put(ENDPOINTS.TABLE_RESERVATIONS.UPDATE(id), reservationData);
    } catch (error) {
      console.error(`Failed to update reservation ${id}:`, error);
      throw error;
    }
  },

  /**
   * Get reservation by ID
   */
  getById: async (id) => {
    try {
      return await apiClient.get(ENDPOINTS.TABLE_RESERVATIONS.GET_BY_ID(id));
    } catch (error) {
      console.error(`Failed to fetch reservation ${id}:`, error);
      throw error;
    }
  },

  /**
   * Delete a reservation
   */
  delete: async (id) => {
    try {
      return await apiClient.delete(ENDPOINTS.TABLE_RESERVATIONS.DELETE(id));
    } catch (error) {
      console.error(`Failed to delete reservation ${id}:`, error);
      throw error;
    }
  },

  /**
   * Cancel a reservation
   */
  cancel: async (id) => {
    try {
      return await apiClient.put(ENDPOINTS.TABLE_RESERVATIONS.CANCEL(id));
    } catch (error) {
      console.error(`Failed to cancel reservation ${id}:`, error);
      throw error;
    }
  },

  /**
   * Mark reservation as seated (customer arrived)
   */
  markSeated: async (id) => {
    try {
      return await apiClient.put(ENDPOINTS.TABLE_RESERVATIONS.MARK_SEATED(id));
    } catch (error) {
      console.error(`Failed to mark reservation ${id} as seated:`, error);
      throw error;
    }
  },

  /**
   * Mark reservation as completed
   */
  markCompleted: async (id) => {
    try {
      return await apiClient.put(ENDPOINTS.TABLE_RESERVATIONS.MARK_COMPLETED(id));
    } catch (error) {
      console.error(`Failed to mark reservation ${id} as completed:`, error);
      throw error;
    }
  },

  /**
   * Mark reservation as no-show
   */
  markNoShow: async (id) => {
    try {
      return await apiClient.put(ENDPOINTS.TABLE_RESERVATIONS.MARK_NO_SHOW(id));
    } catch (error) {
      console.error(`Failed to mark reservation ${id} as no-show:`, error);
      throw error;
    }
  },

  /**
   * Get reservations by date
   */
  getByDate: async (date) => {
    try {
      return await apiClient.get(ENDPOINTS.TABLE_RESERVATIONS.BY_DATE(date));
    } catch (error) {
      console.error(`Failed to fetch reservations for date ${date}:`, error);
      throw error;
    }
  },

  /**
   * Get reservations for a table on a date
   */
  getByTableAndDate: async (tableId, date) => {
    try {
      return await apiClient.get(ENDPOINTS.TABLE_RESERVATIONS.BY_TABLE(tableId, date));
    } catch (error) {
      console.error(`Failed to fetch reservations for table ${tableId} on ${date}:`, error);
      throw error;
    }
  },

  /**
   * Get all upcoming reservations
   */
  getUpcoming: async () => {
    try {
      return await apiClient.get(ENDPOINTS.TABLE_RESERVATIONS.UPCOMING);
    } catch (error) {
      console.error('Failed to fetch upcoming reservations:', error);
      throw error;
    }
  },

  /**
   * Get today's reservations
   */
  getToday: async () => {
    try {
      return await apiClient.get(ENDPOINTS.TABLE_RESERVATIONS.TODAY);
    } catch (error) {
      console.error('Failed to fetch today\'s reservations:', error);
      throw error;
    }
  },

  /**
   * Check if a table is currently blocked due to a reservation
   */
  isTableBlocked: async (tableId) => {
    try {
      const response = await apiClient.get(ENDPOINTS.TABLE_RESERVATIONS.IS_BLOCKED(tableId));
      return response?.data || false;
    } catch (error) {
      console.error(`Failed to check if table ${tableId} is blocked:`, error);
      return false;
    }
  },

  /**
   * Get the reservation currently blocking a table (if any)
   */
  getBlockingReservation: async (tableId) => {
    try {
      return await apiClient.get(ENDPOINTS.TABLE_RESERVATIONS.GET_BLOCKING(tableId));
    } catch (error) {
      // No blocking reservation is not an error
      if (error.response?.status === 204) {
        return null;
      }
      console.error(`Failed to get blocking reservation for table ${tableId}:`, error);
      return null;
    }
  },
};

export default reservationService;
